import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Lock, Mail, User, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
    { met: /[!@#$%^&*]/.test(password), text: 'One special character' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordRequirements.every(req => req.met)) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, fullName);
      toast.success('Account created! Please sign in.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout min-h-screen">
      {/* Left side - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1759661881353-5b9cc55e1cf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGN5YmVyJTIwc2VjdXJpdHklMjBuZXR3b3JrJTIwZGFyayUyMGJsdWUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDgwNjk0Nnww&ixlib=rb-4.1.0&q=85"
          alt="Cyber security"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1C2D]/90 via-[#0B1C2D]/70 to-[#2BBBAD]/30" />
        <div className="relative z-10 h-full flex flex-col justify-center p-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-black text-[#E6F1FF] mb-6">
              Secure Your Digital Life
            </h2>
            <div className="space-y-4">
              {[
                'One-tap emergency lockdown',
                'Multi-account protection',
                'Advanced 2FA security',
                'Real-time audit logging',
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 animate-slide-up" style={{ animationDelay: `${i * 100}ms` }}>
                  <div className="w-8 h-8 rounded-lg bg-[#2BBBAD]/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#2BBBAD]" />
                  </div>
                  <span className="text-[#E6F1FF]">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-[#0B1C2D]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2BBBAD] to-[#64FFDA] flex items-center justify-center shadow-lg shadow-[#2BBBAD]/20">
              <Shield className="w-7 h-7 text-[#0B1C2D]" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#E6F1FF] tracking-tight">PanicLock</h1>
              <p className="text-sm text-[#8892B0]">Unified Digital Emergency Lock</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-[#E6F1FF] mb-2">Create account</h2>
          <p className="text-[#8892B0] mb-8">Start protecting your digital assets</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-[#E6F1FF]">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  className="pl-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#E6F1FF]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#E6F1FF]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  className="pl-11 pr-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8892B0] hover:text-[#E6F1FF]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Password requirements */}
              {password && (
                <div className="mt-3 p-3 rounded-lg bg-[#112240]/50 border border-[#64FFDA]/10">
                  <div className="grid grid-cols-2 gap-2">
                    {passwordRequirements.map((req, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.met ? 'bg-[#10B981]' : 'bg-[#1A2F4B]'}`}>
                          {req.met && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className={`text-xs ${req.met ? 'text-[#10B981]' : 'text-[#8892B0]'}`}>
                          {req.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#E6F1FF]">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="pl-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-[#FF3B30]">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold rounded-lg shadow-lg shadow-[#2BBBAD]/20 hover:shadow-[#2BBBAD]/40 transition-all duration-200"
              data-testid="register-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#8892B0]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2BBBAD] hover:text-[#64FFDA] font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
